import type { SharedShot } from '../net/protocol';
import type { BallId, VariantId } from '../config';

/**
 * Shot permalinks: a whole shot packed into a URL so it can be sent to someone
 * who isn't in the room — or isn't a player at all.
 *
 * The engine is deterministic, so a shot IS its pre-shot layout plus the cue
 * that struck it: a few dozen numbers. That fits in a URL, which means a shared
 * shot needs no server route, no database row and no expiry — the link itself
 * carries the shot, and an old link keeps working forever.
 *
 * The payload rides in the FRAGMENT (`#s=…`), which browsers never send to the
 * server. That keeps shot links out of access logs and lets the existing static
 * `dist/` deployment serve them with no new endpoint.
 */

/** Payload version. Bump only on a breaking field change; readers reject others. */
const VERSION = 1;
/** Fragment key holding the packed shot. */
const PARAM = 's';

/** Wire form — one-letter keys, since every byte lands in a pasted URL. */
interface Packed {
  /** version */
  v: number;
  /** variant: 3 = three-cushion, 4 = straight rail (Korean 4구) */
  g: number;
  /** cue ball id */
  b: string;
  /** cue: [azimuth, elevation, speed, tip a, tip b] */
  c: number[];
  /** positions: [id, x, y] per ball */
  p: [string, number, number][];
  /** caption */
  l?: string;
}

const VARIANT_CODE: Record<VariantId, number> = { 'three-cushion': 3, 'straight-rail': 4 };
const CODE_VARIANT: Record<number, VariantId> = { 3: 'three-cushion', 4: 'straight-rail' };
const BALL_IDS: readonly string[] = ['white', 'yellow', 'red', 'red2'];

/** Table geometry is metres and cue values are radians/m·s⁻¹, so 4 decimals is
 *  far below what anyone can see — and it keeps the link short. */
const r4 = (n: number): number => Math.round(n * 1e4) / 1e4;

function toBase64Url(s: string): string {
  // btoa is byte-oriented; the caption can be Korean, so UTF-8 encode first.
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const byte of bytes) bin += String.fromCharCode(byte);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(s: string): string | null {
  try {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Pack a shot into the opaque token that rides in the link. */
export function encodeShot(shot: SharedShot): string {
  const c = shot.cue;
  const packed: Packed = {
    v: VERSION,
    g: VARIANT_CODE[shot.variant],
    b: c.ball,
    c: [r4(c.azimuth), r4(c.elevation), r4(c.speed), r4(c.tipOffset.a), r4(c.tipOffset.b)],
    p: Object.entries(shot.positions)
      .filter(([id]) => BALL_IDS.includes(id))
      .map(([id, p]) => [id, r4(p.x), r4(p.y)] as [string, number, number]),
  };
  if (shot.label) packed.l = shot.label.slice(0, 40);
  return toBase64Url(JSON.stringify(packed));
}

const finite = (n: unknown, cap: number): number | null =>
  Number.isFinite(Number(n)) ? Math.max(-cap, Math.min(cap, Number(n))) : null;

/**
 * Unpack a token back into a shot, or null if it isn't one.
 *
 * Validated as strictly as the server validates a shared shot in chat: a link is
 * attacker-supplied input that gets fed straight to the physics engine, and a
 * NaN or an out-of-range position would put balls outside the table (or hang the
 * simulation) rather than merely look wrong.
 */
export function decodeShot(token: string): SharedShot | null {
  const json = fromBase64Url(token);
  if (!json) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Partial<Packed>;
  if (p.v !== VERSION) return null;

  const variant = CODE_VARIANT[Number(p.g)];
  if (!variant) return null;
  if (typeof p.b !== 'string' || !BALL_IDS.includes(p.b)) return null;

  const c = Array.isArray(p.c) ? p.c : [];
  const azimuth = finite(c[0], 100);
  const elevation = finite(c[1], 2);
  const speed = finite(c[2], 20);
  const a = finite(c[3], 1);
  const b = finite(c[4], 1);
  if ([azimuth, elevation, speed, a, b].some((v) => v === null)) return null;

  const positions: Record<string, { x: number; y: number }> = {};
  for (const entry of Array.isArray(p.p) ? p.p : []) {
    if (!Array.isArray(entry)) continue;
    const [id, x, y] = entry;
    if (typeof id !== 'string' || !BALL_IDS.includes(id)) continue;
    const px = finite(x, 3);
    const py = finite(y, 3);
    if (px === null || py === null) continue;
    positions[id] = { x: px, y: py };
  }
  if (Object.keys(positions).length < 3) return null; // not a playable layout
  if (!positions[p.b]) return null; // the cue ball must be on the table

  return {
    variant,
    positions,
    cue: {
      ball: p.b as BallId,
      azimuth: azimuth!,
      elevation: elevation!,
      speed: speed!,
      tipOffset: { a: a!, b: b! },
    },
    label: typeof p.l === 'string' ? p.l.slice(0, 40) : '',
  };
}

/** The absolute link to hand someone. Points at wherever this build is served
 *  from, so it works the same on localhost and on the public host. */
export function shotLinkUrl(shot: SharedShot): string {
  const base = location.origin + location.pathname;
  return `${base}#${PARAM}=${encodeShot(shot)}`;
}

/**
 * The shot this page was opened with, if any. Consumes the fragment (a reload,
 * or a later share from this same tab, shouldn't re-trigger the viewer) while
 * leaving the rest of the URL untouched.
 */
export function takeLinkedShot(): SharedShot | null {
  const hash = location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const token = params.get(PARAM);
  if (!token) return null;
  params.delete(PARAM);
  const rest = params.toString();
  history.replaceState(null, '', location.pathname + location.search + (rest ? '#' + rest : ''));
  return decodeShot(token);
}
